import React, {MutableRefObject, useContext, useEffect, useState} from 'react';
import {useTypedFastStorage} from '../../hooks/useFastStorage';
import SecondaryButton from "../../ui/SecondaryButton";
import {ApplicableTableSettings} from "../../tableSettings";
import SmartTable from "../../uiControls/SmartTable";
import {deleteHistoryTable, deleteHistoryTable_BTDo, deleteHistoryTable_BTLoad, deleteHistoryTable_BTOrder} from "./tableSettings";
import {getTbNameByJobName, JobNames} from "../../framework.core/JobNames";
import { BLHistoryItem } from '../../fms.core/BLHistoryItem';
import ApplicationContext from '../../ApplicationContext';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { ApiResult } from '../../framework.core/data/ApiResult';
import { DateRenderer, DateTimeRenderer, NumberedIndicatorRenderer, TextAlignLeftRenderer } from '../../uiControls/SmartTableRenderers';
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import Date from '../../ui/Date';
import { compareIso, getCurrentYear, getDate, getDiffDays, now } from '../../dateUtils';
interface DeleteHistoryStorageKeys {
  modelStorage: string,
};

interface DeleteHistoryParams {
  onPopupClose?: () => void,
  jobName: JobNames,
};

interface DeleteHistoryLoadedParams {
  historyView: BLHistoryItem[],
  historyViewRef: MutableRefObject<BLHistoryItem[] | null>,
  setHistoryView: (x: Partial<BLHistoryItem[]>) => void,
  jobName: JobNames,
  onPopupClose?: () => void,
  defaultDate: string | null,
  fromDate: string | null,
  toDate: string | null,
  setFromDate: React.Dispatch<React.SetStateAction<string | null>>
  setToDate: React.Dispatch<React.SetStateAction<string | null>>,
  removeFromStorage: () => void,
};

function AgentRenderer({row, data}:any) {
  const l = data[row.index];
  const tbName: string = l.tbName;
  const value: string = tbName.substring(4, 5) === 'M' ? l.agentName : l.comName;

  return (
    <div className="ifms-table-align-left">
      {value}
    </div>
  );
}

function BlNoRenderer({row, data}:any) {
  const l = data[row.index];
  const value: string = l.tbName === 'T_GENMAIN' ? l.type : l.blNo;

  return (
    <div className="ifms-table-align-left">
      {value}
    </div>
  );
}

function getDeleteHistoryStorageKeys(): DeleteHistoryStorageKeys {
  return {modelStorage: 'common-history-view'}
}

function DeleteHistoryImpl(params: DeleteHistoryParams) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const {modelStorage} = getDeleteHistoryStorageKeys();
  const {onPopupClose, jobName} = params;

  const [inStorage, setInStorage, removeFromStorage, storageRef] = useTypedFastStorage<BLHistoryItem[]>(modelStorage,[]);

  const defaultDate = getDate(`${getCurrentYear()}-01-01T00:00:00`);
  const [fromDate, setFromDate] = useState<string| null>(defaultDate);
  const [toDate, setToDate] = useState<string| null>(now());


  useEffect(() => {
      initialize();
      return () => removeFromStorage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobName]
  );

  async function loadExisting(): Promise<BLHistoryItem[]> {
    const apiResult:ApiResult<BLHistoryItem[]> = await appContext.api.getDeleteHistory(getTbNameByJobName(jobName), fromDate, toDate);
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (onPopupClose) {
        onPopupClose();
      }
    }
    return apiResult.result;
  }

  async function initialize() {
    const data = await loadExisting();
    setInStorage(data)
  }

  if (!inStorage) {
    return null;
  }

  return (
    <DeleteHistoryInitializedImpl
      historyView={inStorage}
      historyViewRef={storageRef}
      setHistoryView={setInStorage}
      onPopupClose={params.onPopupClose}
      jobName={jobName}
      defaultDate={defaultDate}
      fromDate={fromDate}
      toDate={toDate}
      setFromDate={setFromDate}
      setToDate={setToDate}
      removeFromStorage={removeFromStorage}
    />
  );
}

function DeleteHistoryInitializedImpl({
  historyView,
  historyViewRef,
  setHistoryView,
  onPopupClose,
  jobName,
  defaultDate,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  removeFromStorage,
}: DeleteHistoryLoadedParams) {
  const appContext = useContext(ApplicationContext);
  const tbName = getTbNameByJobName(jobName);

  const [showGlobalOkDialog] = useGlobalOkDialog();

  function getBlNoHeader() {
    if (['T_OIMMAIN', 'T_OOMMAIN'].includes(tbName)) {
      return 'MBL #';
    } else if (['T_OIHMAIN', 'T_OOHMAIN'].includes(tbName)) {
      return 'HBL #';
    } else if (['T_AIMMAIN', 'T_AOMMAIN'].includes(tbName)) {
      return 'MAWB #';
    } else if (['T_AIHMAIN', 'T_AOHMAIN'].includes(tbName)) {
      return 'HAWB #';
    } else if (['T_GENMAIN'].includes(tbName)) {
      return 'Category';
    } 
    return '';
  }

  function getAgentHeader() {
   if (tbName.substring(4, 5) === 'M') {
      return 'Agent';
    }
    else {
      return 'Customer';
    } 
  }

  function validAllDeleteHistoryDate() {
    if (!fromDate || !toDate) {
      showGlobalOkDialog("Please enter the Date");
      return false;
    }
  
    if (getDiffDays(toDate, fromDate) < 0) {
      showGlobalOkDialog("Start date cannot be set later than end date.");
      return false;
    }

    return true;
  }

  async function loadHistorysearchData() {
    const isValidData = validAllDeleteHistoryDate();
    if (!isValidData) return;

    const apiResult: ApiResult<BLHistoryItem[]> = await appContext.api.getDeleteHistory(getTbNameByJobName(jobName), fromDate, toDate);

    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (onPopupClose) {
        onPopupClose();
      }

      return null;
    }

    removeFromStorage();

     setHistoryView(
      apiResult.result.length > 0
         ? apiResult.result.map(item => ({...item, tbName }))
         : []
     );

  }

  const tableOptions: ApplicableTableSettings = {
    ...deleteHistoryTable,
    selectable: true,
    height: '420px',
    tableName: 'deleteHistoryTable',
    data: historyView.map(x => ({...x, tbName})),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'actionDate', Cell: DateTimeRenderer },
      { accessor: 'actionBy', Cell: TextAlignLeftRenderer },
      { accessor: 'refNo', Cell: TextAlignLeftRenderer },
      { accessor: 'blNo', Cell: BlNoRenderer, Header: () => <span>{getBlNoHeader()}</span> },
      { accessor: 'pol', Cell: TextAlignLeftRenderer },
      { accessor: 'etd', Cell: DateRenderer},
      { accessor: 'pod', Cell: TextAlignLeftRenderer },
      { accessor: 'eta', Cell: DateRenderer },
      { accessor: 'agentName', Cell: AgentRenderer, Header: () => <span>{getAgentHeader()}</span> },
    ],
  };

  const tableOptions_BTLoad: ApplicableTableSettings = {
    ...deleteHistoryTable_BTLoad,
    selectable: true,
    height: '400px',
    tableName: 'deleteHistoryTable',
    data: historyView.map(x => ({...x, tbName})),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'actionDate', Cell: DateTimeRenderer },
      { accessor: 'actionBy', Cell: TextAlignLeftRenderer},
      { accessor: 'refNo', Cell: TextAlignLeftRenderer  },
      { accessor: 'etd', Cell: DateRenderer },
      { accessor: 'pol', Cell: TextAlignLeftRenderer},
      { accessor: 'pod', Cell: TextAlignLeftRenderer },
      { accessor: 'blNo', Cell: BlNoRenderer },
      { accessor: 'eta', Cell: DateRenderer },
      { accessor: 'agentName', Cell: AgentRenderer},
    ],
  };

  const tableOptions_BTOrder: ApplicableTableSettings = {
    ...deleteHistoryTable_BTOrder,
    selectable: true,
    height: '400px',
    tableName: 'deleteHistoryTable',
    data: historyView.map(x => ({...x, tbName})),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'actionDate', Cell: DateTimeRenderer },
      { accessor: 'actionBy', Cell: TextAlignLeftRenderer },
      { accessor: 'refNo', Cell: TextAlignLeftRenderer,  },
      { accessor: 'agentName', Cell: TextAlignLeftRenderer },
      { accessor: 'pol', Cell: TextAlignLeftRenderer},
      { accessor: 'etd', Cell: DateRenderer},
      { accessor: 'pod', Cell: TextAlignLeftRenderer },
      { accessor: 'eta', Cell: DateRenderer},
      { accessor: 'comName', Cell: TextAlignLeftRenderer,},
    ],
  };

  const tableOptions_BTDo: ApplicableTableSettings = {
    ...deleteHistoryTable_BTDo,
    selectable: true,
    height: '400px',
    tableName: 'deleteHistoryTable',
    data: historyView.map(x => ({...x, tbName})),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'actionDate', Cell: DateTimeRenderer },
      { accessor: 'actionBy', Cell: TextAlignLeftRenderer},
      { accessor: 'refNo', Cell: TextAlignLeftRenderer },
      { accessor: 'etd', Cell: DateRenderer },
      { accessor: 'pol', Cell: TextAlignLeftRenderer},
      { accessor: 'pod', Cell: TextAlignLeftRenderer },
      { accessor: 'agentName', Cell: TextAlignLeftRenderer },
      { accessor: 'eta', Cell: DateRenderer},
      { accessor: 'comName', Cell: TextAlignLeftRenderer},
    ],
  };

  return (
    <div className='report-popup'>
      <div style={{ display: 'flex', marginTop: '-20px' }}>
        <div style={{ width: '840px' }}>
          <>&nbsp;</>

          <InlineInputControlRow label='Date' mode='small'>
            <div style={{ display: 'flex', marginLeft: '-75px' }}>
              <div style={{ width: '80px' }}>
                <Date
                  dateId='delete-history-fromDate'
                  value={fromDate ?? defaultDate}
                  onChange={(selectedFromDate) => {
                    if (selectedFromDate && compareIso(getDate(selectedFromDate) ?? '', getDate(now()) ?? '') > 0) {
                      showGlobalOkDialog('You cannot enter future date.');
                      setFromDate(defaultDate);
                    } else {
                      setFromDate(selectedFromDate);
                    }
                  }}
                />
              </div>

              <div style={{ width: '60px', paddingLeft: '32px' }}>
                ~
              </div>

              <div style={{ width: '80px' }}>
                <Date
                  dateId='delete-history-toDate'
                  value={toDate ?? now()}
                  onChange={(selectedToDate) => {
                    if (selectedToDate && compareIso(getDate(selectedToDate) ?? '', getDate(now()) ?? '') > 0) {
                      showGlobalOkDialog('You cannot enter future date.');
                      setToDate(now());
                    } else {
                      setToDate(selectedToDate);
                    }
                  }}
                />
              </div>

              <div className='ml-4'>
                <SecondaryButton focusable={true} style={{ width: 60, marginRight: 80 }} size='extrasmall' onClick={async () => {loadHistorysearchData() }}>List</SecondaryButton>
              </div>
            </div>
          </InlineInputControlRow>
        </div>
      </div>

      {tbName === 'T_BTLOAD' &&  <SmartTable {...tableOptions_BTLoad} /> }
      {tbName === 'T_BTORDER' && <SmartTable {...tableOptions_BTOrder} />}
      {tbName === 'T_BTDO' && <SmartTable {... tableOptions_BTDo}/>}
      {!(['T_BTLOAD' , 'T_BTDO', 'T_BTORDER'].includes(tbName)) && <SmartTable {...tableOptions} /> }

      <div className='report-footer'>
        <SecondaryButton
          name = "report-delete-history-close"
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          CLOSE
        </SecondaryButton>
      </div>
    </div>
  );
}

const DeleteHistoryDetails = ({
  onPopupClose,
  jobName,
}: { onPopupClose?: () => void, jobName: JobNames }) => {
  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <DeleteHistoryImpl jobName={jobName} onPopupClose={onPopupClose} key={`deleteHistory-details`}/>
  );
};
export default DeleteHistoryDetails;
